/**
 * ProceduresFields — shared CRUD for user_procedures + ticket + main offer.
 *
 * Self-contained: manages its own Supabase queries/mutations. Caller passes
 * `ticket_medio_cents` + `main_offer` as controlled props since they live in
 * user_business_context (autosaved by parent).
 *
 * Used by both ProceduresStep (wizard) and Perfil (full-edit).
 */

import { useCallback, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PencilSimple as Pencil, Plus, Trash as Trash2 } from "@phosphor-icons/react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useSupabaseClient } from "@/lib/supabase";
import { useProfileId } from "../../hooks/useProfileId";
import type { Database } from "@/types/supabase";

type Procedure = Database["public"]["Tables"]["user_procedures"]["Row"];
type ProcedureFormat = "presencial" | "online" | "hibrido";

interface ProcedureForm {
  name: string;
  value_cents: string;
  duration_minutes: string;
  format: ProcedureFormat | "";
}

const EMPTY_FORM: ProcedureForm = { name: "", value_cents: "", duration_minutes: "", format: "" };

const FORMAT_LABEL: Record<ProcedureFormat, string> = {
  presencial: "Presencial",
  online: "Online",
  hibrido: "Híbrido",
};

export function centsToDisplay(cents: number | null): string {
  if (cents == null) return "";
  return (cents / 100).toFixed(2).replace(".", ",");
}

export function displayToCents(v: string): number | null {
  const cleaned = v.replace(/[^\d,]/g, "").replace(",", ".");
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : Math.round(n * 100);
}

export interface ProceduresFieldsProps {
  ticketDisplay: string;
  onTicketChange: (display: string, cents: number | null) => void;
  mainOffer: string;
  onMainOfferChange: (next: string) => void;
  onProceduresCountChange?: (count: number) => void;
}

export function ProceduresFields({
  ticketDisplay,
  onTicketChange,
  mainOffer,
  onMainOfferChange,
  onProceduresCountChange,
}: ProceduresFieldsProps) {
  const { client, isReady: supabaseReady } = useSupabaseClient();
  const { profileId } = useProfileId();
  const qc = useQueryClient();

  const [form, setForm] = useState<ProcedureForm>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const queryKey = useMemo(
    () => ["user-procedures", profileId ?? "anon", supabaseReady],
    [profileId, supabaseReady],
  );

  const proceduresQuery = useQuery<Procedure[]>({
    queryKey,
    enabled: Boolean(profileId) && supabaseReady,
    staleTime: 0,
    refetchOnMount: "always",
    queryFn: async () => {
      if (!profileId) return [];
      const { data, error } = await client
        .from("user_procedures")
        .select("*")
        .eq("user_id", profileId)
        .order("position", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Procedure[];
    },
  });

  const procedures = proceduresQuery.data ?? [];

  const lastNotifiedCount = useRef<number | null>(null);
  if (onProceduresCountChange && lastNotifiedCount.current !== procedures.length) {
    lastNotifiedCount.current = procedures.length;
    onProceduresCountChange(procedures.length);
  }

  const insertMutation = useMutation({
    mutationFn: async (f: ProcedureForm) => {
      if (!profileId) throw new Error("Profile not ready");
      const maxPos = procedures.reduce((m, p) => Math.max(m, p.position), -1);
      const { error } = await client.from("user_procedures").insert({
        user_id: profileId,
        name: f.name.trim(),
        value_cents: displayToCents(f.value_cents),
        duration_minutes: f.duration_minutes ? parseInt(f.duration_minutes, 10) : null,
        format: f.format || null,
        position: maxPos + 1,
      });
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey }),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, f }: { id: string; f: ProcedureForm }) => {
      const { error } = await client
        .from("user_procedures")
        .update({
          name: f.name.trim(),
          value_cents: displayToCents(f.value_cents),
          duration_minutes: f.duration_minutes ? parseInt(f.duration_minutes, 10) : null,
          format: f.format || null,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await client.from("user_procedures").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey }),
  });

  const handleSave = useCallback(async () => {
    if (!form.name.trim()) return;
    if (editingId) {
      await updateMutation.mutateAsync({ id: editingId, f: form });
      setEditingId(null);
    } else {
      await insertMutation.mutateAsync(form);
    }
    setForm(EMPTY_FORM);
    setShowForm(false);
  }, [form, editingId, insertMutation, updateMutation]);

  const handleEdit = useCallback((p: Procedure) => {
    setForm({
      name: p.name,
      value_cents: centsToDisplay(p.value_cents),
      duration_minutes: p.duration_minutes != null ? String(p.duration_minutes) : "",
      format: (p.format as ProcedureFormat | null) ?? "",
    });
    setEditingId(p.id);
    setShowForm(true);
  }, []);

  const handleCancel = useCallback(() => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  }, []);

  return (
    <div className="space-y-4">
      {procedures.length > 0 && (
        <div className="space-y-2">
          {procedures.map((p) => (
            <div
              key={p.id}
              className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2.5 text-sm"
            >
              <div className="min-w-0 flex-1">
                <span className="font-medium text-foreground">{p.name}</span>
                <div className="mt-0.5 flex flex-wrap gap-2 text-xs text-muted-foreground">
                  {p.value_cents != null && <span>R$ {centsToDisplay(p.value_cents)}</span>}
                  {p.duration_minutes != null && <span>{p.duration_minutes} min</span>}
                  {p.format && <span>{FORMAT_LABEL[p.format as ProcedureFormat]}</span>}
                </div>
              </div>
              <div className="ml-2 flex shrink-0 gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => handleEdit(p)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  onClick={() => deleteMutation.mutate(p.id)}
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm ? (
        <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="proc-name">Nome do procedimento *</Label>
            <Input
              id="proc-name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Ex: Consulta de retorno, Protocolo facial, Curso online"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="proc-value">Valor (R$)</Label>
              <Input
                id="proc-value"
                value={form.value_cents}
                onChange={(e) => setForm((f) => ({ ...f, value_cents: e.target.value }))}
                placeholder="0,00"
                inputMode="decimal"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="proc-duration">Duração (min)</Label>
              <Input
                id="proc-duration"
                type="number"
                min={0}
                value={form.duration_minutes}
                onChange={(e) => setForm((f) => ({ ...f, duration_minutes: e.target.value }))}
                placeholder="60"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Formato</Label>
            <Select
              value={form.format}
              onValueChange={(v) =>
                setForm((f) => ({ ...f, format: v as ProcedureFormat | "" }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecionar formato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="presencial">Presencial</SelectItem>
                <SelectItem value="online">Online</SelectItem>
                <SelectItem value="hibrido">Híbrido</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={handleCancel}>
              Cancelar
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={
                !form.name.trim() || insertMutation.isPending || updateMutation.isPending
              }
            >
              {editingId ? "Salvar" : "Adicionar"}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="w-full border-dashed"
          onClick={() => setShowForm(true)}
        >
          <Plus className="mr-1.5 h-4 w-4" />
          Adicionar procedimento
        </Button>
      )}

      <div className="space-y-1.5 border-t border-border pt-4">
        <Label htmlFor="ticket-medio">Ticket médio (R$)</Label>
        <Input
          id="ticket-medio"
          value={ticketDisplay}
          onChange={(e) => onTicketChange(e.target.value, displayToCents(e.target.value))}
          placeholder="Ex: 350,00"
          inputMode="decimal"
        />
        <p className="text-xs text-muted-foreground">
          Valor médio por cliente ou atendimento. Usado para contextualizar as comunicações.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="main-offer">Oferta principal (opcional)</Label>
        <Textarea
          id="main-offer"
          value={mainOffer}
          onChange={(e) => onMainOfferChange(e.target.value)}
          placeholder="Ex: Plano de tratamento facial completo em 3 sessões — R$ 3.500."
          rows={2}
          className="resize-none"
        />
        <p className="text-xs text-muted-foreground">
          Seu serviço/produto carro-chefe. Ajuda a IA a destacar o que você quer vender mais.
        </p>
      </div>
    </div>
  );
}
